import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Newspaper,
  MessageSquare,
  Briefcase,
  Inbox,
  Users,
  LogOut,
  ShieldCheck,
} from "lucide-react";

import { COMPANY_INFO } from "../../data/mock";
import ChangePasswordDialog from "./ChangePasswordDialog";


const NAV_ITEMS = [
  {
    label: "Blogs",
    icon: Newspaper,
    path: "/admin/blogs",
    match: (pathname) => pathname.startsWith("/admin/blog"),
  },
  {
    label: "Job Post",
    icon: Briefcase,
    path: "/admin/jobs",
    match: (pathname) =>
      pathname.startsWith("/admin/job") &&
      !pathname.startsWith("/admin/job-applicants"),
  },
  {
    label: "Job Applicants",
    icon: Inbox,
    path: "/admin/job-applicants",
    match: (pathname) => pathname.startsWith("/admin/job-applicants"),
  },
  {
    label: "Contact Us",
    icon: MessageSquare,
    path: "/admin/contacts",
    match: (pathname) => pathname.startsWith("/admin/contact"),
  },
  {
    label: "Users",
    icon: Users,
    path: "/admin/users",
    match: (pathname) => pathname.startsWith("/admin/user"),
  },
];

// Sections a plain "user" is allowed to see. Admins (and any other role)
// see the full menu.
const USER_ALLOWED = ["Blogs"];

// Resolve the role from the `user` prop, falling back to localStorage. On a
// page refresh the prop is briefly null (AdminLayout loads it async), so the
// localStorage fallback keeps the filtered menu correct on the first paint —
// otherwise the hidden items flash in before the role is known.
const resolveRole = (user) => {
  if (user?.role) return user.role.toLowerCase();
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    return (stored?.role || "").toLowerCase();
  } catch (e) {
    return "";
  }
};

const AdminSidebar = ({ open, user, onLogout, onPasswordChanged }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [changePwOpen, setChangePwOpen] = useState(false);

  // Restrict the menu for the "user" role.
  const isUser = resolveRole(user) === "user";
  const navItems = isUser
    ? NAV_ITEMS.filter((item) => USER_ALLOWED.includes(item.label))
    : NAV_ITEMS;

  const rowClass = (extra = "") =>
    `w-full flex items-center rounded-lg transition-colors py-2.5 ${
      open ? "gap-3 px-4" : "justify-center px-0"
    } ${extra}`;

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-20 flex flex-col bg-gray-900 text-white transition-all duration-300 ${
        open ? "w-64" : "w-20"
      }`}
    >
      
      <div
        className={`flex items-center h-16 border-b border-white/10 ${
          open ? "px-4" : "justify-center px-0"
        }`}
      >
        {open ? (
          <span className="inline-flex items-center rounded-lg bg-white px-2 py-1.5 shadow">
            <img
              src={COMPANY_INFO.logo}
              alt={COMPANY_INFO.name}
              className="h-7 w-auto"
            />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-white shadow">
            <img
              src="/favicon.png"
              alt={COMPANY_INFO.name}
              className="h-8 w-8 object-contain"
            />
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 hover:[&::-webkit-scrollbar-thumb]:bg-white/30">
        <ul className="space-y-1">
          {navItems.map(({ label, icon: Icon, path, match }) => {
            const active = match ? match(pathname) : pathname === path;
            return (
              <li key={label}>
                <button
                  onClick={() => navigate(path)}
                  title={label}
                  className={rowClass(
                    active
                      ? "bg-white/10 text-white border-l-2 border-[#b32126]"
                      : "text-white/85 hover:bg-white/10 hover:text-white"
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {open && <span>{label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Fixed bottom: super admin info + logout */}
      <div className="border-t border-white/10 p-3">
        <button
          type="button"
          onClick={() => setChangePwOpen(true)}
          title="Change password"
          className={`w-full flex items-center mb-3 rounded-lg py-1 hover:bg-white/5 transition-colors ${
            open ? "gap-3 px-1" : "justify-center"
          }`}
        >
          <div className="h-9 w-9 shrink-0 rounded-full bg-[#b32126]/20 text-[#ff5a5f] flex items-center justify-center font-semibold">
            {(user?.name || "S").charAt(0).toUpperCase()}
          </div>
          {open && (
            <div className="min-w-0 text-left">
              <p className="text-sm font-medium text-white truncate">
                {user?.name}
              </p>
              <p className="text-xs text-white/50 flex items-center gap-1 truncate">
                <ShieldCheck className="h-3 w-3 shrink-0" />
                {user?.role}
              </p>
            </div>
          )}
        </button>

        <button
          onClick={onLogout}
          title="Logout"
          className={rowClass(
            "bg-white/5 text-white/85 hover:bg-[#b32126] hover:text-white"
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {open && <span>Logout</span>}
        </button>
      </div>

      <ChangePasswordDialog
        open={changePwOpen}
        onOpenChange={setChangePwOpen}
        user={user}
        onSuccess={onPasswordChanged}
      />
    </aside>
  );
};

export default AdminSidebar;
